
import { useState } from 'react';
import { flashDeals } from '../../../mocks/promoDeals';

export default function FlashDeals() {
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const handleClaim = async (dealId: string) => {
    setLoadingId(dealId);
    try {
      const response = await fetch(
        'https://puoyglsrhhxtslsqwexu.supabase.co/functions/v1/create-subscription',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ planName: dealId, isYearly: true }),
        }
      );
      const data = await response.json().catch(() => { throw new Error('Invalid response'); });
      if (data.success && data.url) {
        window.location.href = data.url;
      }
    } catch {
      // Fallback
    } finally {
      setLoadingId(null);
    }
  };

  const badgeColors: Record<string, string> = {
    'Best Seller': 'bg-amber-500',
    'Top Value': 'bg-emerald-500',
    'Limited': 'bg-red-500',
  };

  return (
    <section id="flash-deals" className="py-24 bg-slate-950">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-full px-4 py-1.5 mb-4">
            <i className="ri-fire-fill text-red-400 w-4 h-4 flex items-center justify-center"></i>
            <span className="text-red-300 text-sm font-semibold">Flash Deals</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white font-display mb-4">
            Limited-Time Plan Discounts
          </h2>
          <p className="text-slate-400 max-w-xl mx-auto">
            Lock in these prices before they expire. Once they&apos;re gone, they&apos;re gone.
          </p>
        </div>

        {/* Deal Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {flashDeals.map((deal, idx) => (
            <div
              key={deal.id}
              className={`relative rounded-2xl p-8 transition-all hover:-translate-y-1 ${
                idx === 0
                  ? 'bg-gradient-to-b from-amber-500/10 to-slate-900 border-2 border-amber-500/40 ring-1 ring-amber-500/20'
                  : 'bg-slate-900 border border-slate-800'
              }`}
            >
              {/* Badge */}
              <div className="absolute -top-3 left-6">
                <span className={`${badgeColors[deal.badge] || 'bg-amber-500'} text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap`}>
                  {deal.badge}
                </span>
              </div>

              {/* Discount */}
              <div className="flex items-center justify-between mb-5">
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <span className="text-amber-400 font-bold text-lg">{deal.discount}%</span>
                </div>
                <span className="text-slate-500 text-sm">/{deal.period}</span>
              </div>

              {/* Name */}
              <h3 className="text-xl font-bold text-white mb-2">{deal.name}</h3>
              <p className="text-slate-400 text-sm mb-6 leading-relaxed">{deal.description}</p>

              {/* Pricing */}
              <div className="mb-6">
                <div className="flex items-baseline gap-3">
                  <span className="text-3xl font-bold text-white">${deal.salePrice}</span>
                  <span className="text-slate-500 line-through text-lg">${deal.originalPrice}</span>
                </div>
                <p className="text-emerald-400 text-sm font-medium mt-1">
                  You save ${deal.originalPrice - deal.salePrice} AUD
                </p>
              </div>

              {/* Features */}
              <ul className="space-y-2.5 mb-8">
                {deal.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <i className="ri-check-line text-amber-400 mt-0.5 w-4 h-4 flex items-center justify-center shrink-0"></i>
                    <span className="text-slate-300 text-sm">{f}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <button
                onClick={() => handleClaim(deal.id)}
                disabled={loadingId === deal.id}
                className={`w-full py-3.5 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 ${
                  idx === 0
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:shadow-lg hover:shadow-amber-500/20'
                    : 'bg-white/10 text-white border border-white/10 hover:bg-white/20'
                }`}
              >
                {loadingId === deal.id ? 'Processing...' : 'Claim This Deal'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
